import { GM_FLOW_KEY, GM_NAME_KEY } from '../consts/consts';

type GmFlowState = {
    needsName: boolean;
    needsShare: boolean;
};

type GmFlowMap = Record<string, GmFlowState>;
type GmNameMap = Record<string, string>;

function getFlowKey(roomId: string, playerId: string): string {
    return `${roomId}::${playerId}`;
}

function readMap(): GmFlowMap {
    try {
        const raw = localStorage.getItem(GM_FLOW_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        return Object.entries(parsed).reduce<GmFlowMap>((acc, [key, value]) => {
            if (!key || !value || typeof value !== 'object') return acc;
            const state = value as Partial<GmFlowState>;
            acc[key] = {
                needsName: Boolean(state.needsName),
                needsShare: Boolean(state.needsShare),
            };
            return acc;
        }, {});
    } catch {
        return {};
    }
}

function saveMap(map: GmFlowMap) {
    localStorage.setItem(GM_FLOW_KEY, JSON.stringify(map));
}

function readNameMap(): GmNameMap {
    try {
        const raw = localStorage.getItem(GM_NAME_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        return Object.entries(parsed).reduce<GmNameMap>((acc, [key, value]) => {
            if (typeof key !== 'string' || typeof value !== 'string' || !key || !value.trim()) {
                return acc;
            }
            acc[key] = value;
            return acc;
        }, {});
    } catch {
        return {};
    }
}

function saveNameMap(map: GmNameMap) {
    localStorage.setItem(GM_NAME_KEY, JSON.stringify(map));
}

export function markGmRoomCreated(roomId: string, playerId: string) {
    if (!roomId || !playerId) return;
    const map = readMap();
    map[getFlowKey(roomId, playerId)] = {
        needsName: true,
        needsShare: true,
    };
    saveMap(map);
}

export function getGmFlowState(roomId: string, playerId: string): GmFlowState {
    if (!roomId || !playerId) {
        return { needsName: false, needsShare: false };
    }
    const map = readMap();
    return map[getFlowKey(roomId, playerId)] ?? { needsName: false, needsShare: false };
}

export function completeGmNameStep(roomId: string, playerId: string) {
    if (!roomId || !playerId) return;
    const map = readMap();
    const key = getFlowKey(roomId, playerId);
    const prev = map[key] ?? { needsName: false, needsShare: false };
    map[key] = {
        ...prev,
        needsName: false,
    };
    saveMap(map);
}

export function completeGmShareStep(roomId: string, playerId: string) {
    if (!roomId || !playerId) return;
    const map = readMap();
    const key = getFlowKey(roomId, playerId);
    const prev = map[key] ?? { needsName: false, needsShare: false };
    map[key] = {
        ...prev,
        needsShare: false,
    };
    saveMap(map);
}

export function getStoredGmName(roomId: string, playerId: string): string | null {
    if (!roomId || !playerId) return null;
    const key = getFlowKey(roomId, playerId);
    const map = readNameMap();
    const value = map[key];
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function setStoredGmName(roomId: string, playerId: string, name: string) {
    if (!roomId || !playerId || !name.trim()) return;
    const key = getFlowKey(roomId, playerId);
    const map = readNameMap();
    map[key] = name.trim();
    saveNameMap(map);
}
